import React, {useEffect} from 'react'
import {Controller, FieldErrors} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {Checkbox, Radio, RadioGroup} from '@material-ui/core'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Typography from '@material-ui/core/Typography'
import useStyles from 'components/Dialog/User/Style'
import {useBoldInputStyle} from 'components/common/useBoldInputStyle'
import {PatientAddForm} from 'api/patientApi'
import useCheckupItemViewModel, {
  OnChangeSelectedPackageItems,
} from './hooks/useCheckupItemViewModel'

export interface CheckUpItemsProps {
  control: any
  errors: FieldErrors<PatientAddForm>
  initialSelectedPackageItemIds?: string[]
  onUpdateCheckedPackage?: OnChangeSelectedPackageItems
}

function CheckUpItems({
  control,
  errors,
  initialSelectedPackageItemIds,
  onUpdateCheckedPackage,
}: CheckUpItemsProps) {
  const {t} = useTranslation()
  const classes = useStyles()
  const inputClasses = useBoldInputStyle()
  const {
    availablePackageItems,
    selectedPackageItems,
    handleChangeSelectedPackageItems,
    handleCheckedSelectedPackageItems,
    zScores,
  } = useCheckupItemViewModel({
    initialSelectedPackageItemIds: initialSelectedPackageItemIds ?? [],
    onChangeSelectedPackageItems: onUpdateCheckedPackage,
  })

  return (
    <div className={classes.infoContainer}>
      <Typography className={inputClasses.inputLabel}>
        {t('ICheckupItems')} {t('ICheckupWarn')}
      </Typography>
      <Typography className={classes.smallPadding} />

      <div className={classes.displayController}>
        <Controller
          name='healthProductId'
          key='healthProductId'
          control={control}
          defaultValue={null}
          render={({field}) => (
            <RadioGroup
              row
              className={classes.displayRadioGroup}
              onChange={(event) => {
                handleChangeSelectedPackageItems(event.target.value)
                field.onChange(String(event.target.value))
              }}
            >
              {availablePackageItems
                .filter((pkg) => pkg.target === 'HealthCare')
                .map((item) => (
                  <FormControlLabel
                    key={item.itemId}
                    control={
                      <Radio
                        color='primary'
                        checked={selectedPackageItems.includes(item)}
                      />
                    }
                    label={
                      <p style={{marginRight: '30px'}}>{item.displayInfo}</p>
                    }
                    value={String(item.itemId)}
                  />
                ))}
            </RadioGroup>
          )}
        />
        <p className={classes.contentCheckupNote}>“{t('ICheckupNote')}”</p>
        {availablePackageItems
          .filter(
            (pkg) => pkg.target !== 'HealthCare' && 'EEG_HRV'.includes(pkg.cls),
          )
          .map((item) =>
            item.cls === 'EEG' && item.type === 'Norm' ? (
              <Controller
                name='zscore'
                key='zscore'
                control={control}
                defaultValue={1.96}
                render={({field}) => (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedPackageItems.includes(item)}
                          onChange={(e) =>
                            handleCheckedSelectedPackageItems(
                              e.target.checked,
                              item,
                            )
                          }
                        />
                      }
                      label={t('IAnalysisNorm')}
                    />
                    <RadioGroup
                      row
                      value={field.value ?? 1.96}
                      onChange={(event) =>
                        field.onChange(Number(event.target.value))
                      }
                    >
                      {zScores.map((zItem) => (
                        <FormControlLabel
                          key={zItem.value}
                          control={
                            <Radio
                              color='primary'
                              checked={zItem.value === field.value}
                              disabled={!selectedPackageItems.includes(item)}
                            />
                          }
                          label={
                            <p style={{marginRight: '30px'}}>{zItem.label}</p>
                          }
                          value={zItem.value}
                        />
                      ))}
                    </RadioGroup>
                  </div>
                )}
              />
            ) : (
              <FormControlLabel
                key={`${item.itemId}-${item.cls}`}
                control={
                  <Checkbox
                    checked={selectedPackageItems.includes(item)}
                    onChange={(e) =>
                      handleCheckedSelectedPackageItems(e.target.checked, item)
                    }
                  />
                }
                label={item.displayInfo}
                style={{
                  width: 'fit-content',
                }}
              />
            ),
          )}
      </div>

      {errors.healthProductId && (
        <p className={classes.errorText}>{errors.healthProductId.message}</p>
      )}
    </div>
  )
}

export default React.memo(CheckUpItems)
